import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import CodeMirror from "@uiw/react-codemirror";
import {
  nginxList,
  nginxRead,
  nginxReload,
  nginxTest,
  nginxWrite,
} from "../../redux/actionCreator";
import {
  Check, RotateCw, Globe, ArrowUp, Folder, Link as LinkIcon, File as FileIcon, Settings,
} from "lucide-react";
import "./nginxPanel.scss";

function joinPath(dir, name) {
  if (!dir || dir === "/") return "/" + name;
  return `${dir}/${name}`.replace(/\/+/g, "/");
}
function parentOf(p) {
  if (!p || p === "/") return "/";
  const idx = p.lastIndexOf("/");
  if (idx <= 0) return "/";
  return p.slice(0, idx);
}

/**
 * NGINX raw-config editor: file tree (left) + CodeMirror (right) + Test/Reload
 * in the toolbar. Subdomain lifecycle (add/SSL/disable/delete/split) lives on
 * the dedicated Subdomains page now — this panel is purely for editing
 * arbitrary nginx config files.
 */
export default function NginxPanel({ show, onHide, project, server }) {
  const dispatch = useDispatch();
  const [path, setPath] = useState("/");
  const [entries, setEntries] = useState([]);
  const [basePath, setBasePath] = useState("");
  const [sitesDir, setSitesDir] = useState("");
  const [selected, setSelected] = useState(null); // relative path of file open in editor
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Test/reload output panel.
  const [testOpen, setTestOpen] = useState(false);
  const [testOk, setTestOk] = useState(null); // null | true | false
  const [testOutput, setTestOutput] = useState("");
  const dirtyRef = useRef(false);

  const projectId = project?._id;
  const serverId = server?._id;

  const loadTree = useCallback(
    async (p = path) => {
      if (!projectId || !serverId) return;
      setLoadingTree(true);
      setError(null);
      const res = await dispatch(nginxList(projectId, serverId, p));
      setLoadingTree(false);
      if (res?.status) {
        setEntries(res.data?.entries || []);
        setBasePath(res.data?.base_path || "");
        setSitesDir(res.data?.sites_dir || "");
      } else {
        setEntries([]);
        setError(res?.message || "Failed to list");
      }
    },
    [dispatch, projectId, serverId, path]
  );

  useEffect(() => {
    if (show) {
      setPath("/");
      setSelected(null);
      setContent("");
      setOriginal("");
      setTestOpen(false);
      loadTree("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, projectId, serverId]);

  useEffect(() => {
    if (show) loadTree(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const openFile = async (e) => {
    if (e.is_dir) {
      setPath(joinPath(path, e.name));
      return;
    }
    if (dirtyRef.current && !window.confirm("Discard unsaved changes?")) return;
    const fullRel = joinPath(path, e.name);
    setLoadingFile(true);
    setError(null);
    const res = await dispatch(nginxRead(projectId, serverId, fullRel));
    setLoadingFile(false);
    if (res?.status) {
      setSelected(fullRel);
      setContent(res.data?.content || "");
      setOriginal(res.data?.content || "");
    } else {
      setError(res?.message || "Read failed");
    }
  };

  const dirty = content !== original;
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    const res = await dispatch(nginxWrite(projectId, serverId, selected, content));
    setSaving(false);
    if (res?.status) {
      setOriginal(content);
    } else {
      setError(res?.message || "Save failed");
    }
  };

  const runTest = async () => {
    setTestOpen(true);
    setTestOk(null);
    setTestOutput("Running nginx -t…");
    const res = await dispatch(nginxTest(projectId, serverId));
    setTestOk(!!res?.status);
    setTestOutput(res?.data?.output || res?.message || "");
  };

  const runReload = async () => {
    setTestOpen(true);
    setTestOk(null);
    setTestOutput("Reloading nginx…");
    const res = await dispatch(nginxReload(projectId, serverId));
    setTestOk(!!res?.status);
    setTestOutput(
      res?.status
        ? "nginx reloaded"
        : `${res?.message || "reload failed"}\n\n${res?.data?.test_output || ""}`
    );
  };

  const breadcrumbs = React.useMemo(() => {
    const segs = path.split("/").filter(Boolean);
    const out = [{ label: basePath || "/", p: "/" }];
    let acc = "";
    for (const s of segs) {
      acc = `${acc}/${s}`;
      out.push({ label: s, p: acc });
    }
    return out;
  }, [path, basePath]);

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered fullscreen="lg-down">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className="m-0">NGINX</h4>
            <span className="text-muted">{server?.label}</span>
            <code className="small">{server?.username}@{server?.host}</code>
          </div>
        </Modal.Header>
        <Modal.Body className="nginx-panel-body">
          <div className="nginx-toolbar">
            <Button size="sm" variant="light" onClick={runTest}>
              <Check size={14} className="me-1" /> Test config
            </Button>
            <Button size="sm" variant="warning" onClick={runReload}>
              <RotateCw size={14} className="me-1" /> Reload nginx
            </Button>
            <span className="text-muted ms-auto small">
              <code>{basePath || "/etc/nginx"}</code>
              {sitesDir && (
                <> · vhosts: <code>{sitesDir}</code></>
              )}
              <span className="ms-2 d-inline-flex align-items-center gap-1">· Subdomain CRUD lives on the <Globe size={12} /> Subdomains page.</span>
            </span>
          </div>

          {testOpen && (
            <div className={`nginx-test-output ${testOk === true ? "ok" : testOk === false ? "bad" : ""}`}>
              <div className="d-flex justify-content-between">
                <strong>{testOk === true ? "OK" : testOk === false ? "Failed" : "Running…"}</strong>
                <button type="button" className="btn-close-fake" onClick={() => setTestOpen(false)}>×</button>
              </div>
              <pre>{testOutput}</pre>
            </div>
          )}

          {error && <div className="alert alert-danger m-2">{error}</div>}

          <div className="nginx-split">
            <div className="nginx-tree">
              <div className="nginx-crumbs">
                {path !== "/" && (
                  <button className="up" onClick={() => setPath(parentOf(path))}>
                    <ArrowUp size={14} className="me-1" /> Up
                  </button>
                )}
                {breadcrumbs.map((c, i) => (
                  <React.Fragment key={c.p}>
                    {i > 0 && <span className="sep">/</span>}
                    <button className="crumb" onClick={() => setPath(c.p)}>
                      {c.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
              {loadingTree ? (
                <div className="empty"><Spinner size="sm" animation="border" /></div>
              ) : entries.length === 0 ? (
                <div className="empty">Empty</div>
              ) : (
                <ul>
                  {entries.map((e) => {
                    const sel = !e.is_dir && selected === joinPath(path, e.name);
                    return (
                      <li
                        key={e.name}
                        className={`${e.is_dir ? "dir" : "file"} ${sel ? "selected" : ""}`}
                        onClick={() => openFile(e)}
                      >
                        <span className="icon">{e.is_dir ? <Folder size={14} /> : e.is_link ? <LinkIcon size={14} /> : <FileIcon size={14} />}</span>
                        {e.name}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="nginx-editor">
              {!selected ? (
                <div className="empty">
                  <div>
                    <div style={{ fontSize: 48, opacity: 0.5 }}><Settings size={48} /></div>
                    <div>Pick a config file on the left to edit it</div>
                  </div>
                </div>
              ) : loadingFile ? (
                <div className="empty"><Spinner animation="border" /></div>
              ) : (
                <>
                  <div className="nginx-editor-bar">
                    <code className="small">{selected}</code>
                    {dirty && <span className="badge bg-warning text-dark ms-2">unsaved</span>}
                    <div className="ms-auto d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!dirty || saving}
                        onClick={save}
                      >
                        {saving ? <Spinner size="sm" animation="border" /> : "Save"}
                      </Button>
                    </div>
                  </div>
                  <CodeMirror
                    value={content}
                    height="56vh"
                    extensions={[]}
                    onChange={(v) => setContent(v)}
                    theme="dark"
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: true,
                      highlightActiveLine: true,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
